<template>
  <div class="masonry" ref="masonryRoot">
    <!-- 🔹 SKELETONS iniciales mientras carga la primera página -->
    <template v-if="loading && (!posts || !posts.length)">
      <div
        v-for="n in skeletonCount"
        :key="'sk-'+n"
        class="masonry-item"
      >
        <v-card class="post-card skeleton-height1" elevation="0" color="transparent">
          <v-skeleton-loader type="image" class="post-skeleton"/>
        </v-card>
      </div>
    </template>

    <!-- 🔹 Grid real cuando ya hay posts -->
    <template v-else>
      <div
        v-for="(post, index) in posts"
        :key="index"
        class="masonry-item"
        :class="[ post.provider ? post.provider.replace(/ /g,'_') : '' ]"
      >
        <v-card
          class="post-card"
          elevation="0"
          color="transparent"
          :class="{ 'is-active': isActive(index) }"
          @click="!isTouch && openDialog(post, index)"
          @mouseenter="!isTouch && playVideo(index)"
          @mouseleave="!isTouch && stopVideo(index)"
        >
          <!-- Media -->
          <div class="media">
            <!-- Video -->
            <video
              style="background:rgba(0, 0, 0, 0.12);"
              v-if="isVideo(firstMedia(post))"
              :src="fullUrl(firstMedia(post))"
              :ref="'video-' + index"
              muted
              playsinline
              loop
              preload="metadata"
              @touchstart.stop="onTouchStart(index, $event)"
              @touchmove.stop="onTouchMove($event)"
              @touchend.stop="onTouchEnd(index, $event)"
              @loadedmetadata="onMediaLoaded"
              @loadeddata="onMediaLoaded"
              @error="onMediaLoaded"
              :poster="post.thumbnail ? fullUrl(post.thumbnail) : ''"
            ></video>

            <!-- Imagen -->
            <img
              v-else-if="firstMedia(post)"
              :src="fullUrl(firstMedia(post))"
              alt=""
              @load="onMediaLoaded"
              @error="onMediaLoaded"
            />

            <!-- Fallback cuando no hay media -->
            <div v-else class="media-fallback"></div>
          </div>

          <!-- Máscara: sólo en hover / is-active -->
          <div class="mask"></div>

          <!-- Overlay -->
          <div class="overlay">
            <slot name="overlay" :post="post">
              <div>
                <v-row class="ma-0">
                  <v-avatar size="45" color="red" class="mr-4">
                    <v-img :src="post.influencer.profile_photo_path"/>
                  </v-avatar>
                  <div class="influencer-data-white">
                    <v-card-title class="pa-0 ma-0">
                      {{ post.influencer.name + (post.influencer.last ? (' ' + post.influencer.last) : '') }}
                    </v-card-title>
                    <v-card-subtitle class="pa-0 ma-0">
                      {{ post.provider == "Case Study"
                        ? (post.influencer && (post.influencer.name + (post.influencer.last ? (' ' + post.influencer.last) : '')))
                        : (post.influencer && post.influencer.social_networks
                            ? post.influencer.social_networks[post.provider == "TikTok Content" ? 'tiktok' : 'instagram']
                            : '—'
                          )
                      }}
                      <v-icon x-small color="#0095F6">mdi-check-decagram</v-icon>
                    </v-card-subtitle>
                  </div>
                </v-row>

                <v-chip class="post-provider mt-4" color="white" label>
                  <v-icon x-small class="mr-1" v-if="post.provider && post.provider.toLowerCase().includes('instagram')">mdi-instagram</v-icon>
                  <v-img height="10px" width="10px" class="mr-1" v-else-if="post.provider && post.provider.toLowerCase().includes('tiktok')" src="/tiktok-icon.png"></v-img>
                  {{ post.provider }}
                </v-chip>
              </div>
            </slot>
          </div>
        </v-card>
      </div>
    </template>

    <!-- Dialog -->
    <v-dialog v-model="dialog" width="900">
      <post v-if="dialog" :selected_post="selected_post" @closeDialog="closeDialog" />
    </v-dialog>
  </div>

</template>

<script>
import Post from './Post.vue'

export default {
  components: { post: Post },
  props: {
    posts: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data () {
    return {
      selected_post: undefined,
      selected_index: -1,
      dialog: false,
      FILES_BASE_URL: 'https://peach.blitzhub.mx/files/',
      ro: null,   // ResizeObserver
      vio: null,  // IntersectionObserver para videos
      isTouch: false,
      
      // 👇 Estado para gestionar touch
      activeIndex: null,     // card “activa” en mobile para mostrar overlay
      touchStartX: 0,
      touchStartY: 0,
      lastDx: 0,
      lastDy: 0,
      touchHasMoved: false,
      touchIsHorizontal: false,

      skeletonCount: 8
    }
  },
  mounted () {
    // detectar touch
    this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    this.$nextTick(() => {
      this.setupObservers()
      this.attachMediaListeners()
      this.resizeAll()
      this.mountVideoObserver()
    })
    window.addEventListener('resize', this.onResize, { passive: true })
  },
  updated () {
    this.$nextTick(() => {
      this.setupObservers()
      this.attachMediaListeners()
      this.resizeAll()
      this.mountVideoObserver()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    if (this.ro) this.ro.disconnect()
    if (this.vio) this.vio.disconnect()
  },
  methods: {
    openDialog (post, index) {
      this.selected_post = post
      this.selected_index = index
      this.dialog = true
    },
    closeDialog () {
      this.dialog = false
    },
    


    // ===== Overlay activo en mobile =====
    isActive (index) {
      // Sólo nos importa en mobile
      return this.isTouch && this.activeIndex === index
    },

    // ====== TOUCH (mobile) ======
    onTouchStart (index, e) {
      if (!this.isTouch) return
      const t = e.touches[0]
      this.touchStartX = t.clientX
      this.touchStartY = t.clientY
      this.lastDx = 0
      this.lastDy = 0
      this.touchHasMoved = false
      this.touchIsHorizontal = false
    },

    onTouchMove (e) {
      if (!this.isTouch) return
      const t = e.touches[0]
      const dx = t.clientX - this.touchStartX
      const dy = t.clientY - this.touchStartY
      this.lastDx = dx
      this.lastDy = dy

      if (!this.touchHasMoved && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        this.touchHasMoved = true
      }

      // Consideramos swipe horizontal si se mueve bastante más en X que en Y
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 20) {
        this.touchIsHorizontal = true
      } else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 20) {
        // Claramente vertical: dejamos que el scroll haga lo suyo
        this.touchIsHorizontal = false
      }
    },

    onTouchEnd (index, e) {
      if (!this.isTouch) return

      const absDx = Math.abs(this.lastDx)
      const absDy = Math.abs(this.lastDy)

      // 1) Swipe horizontal claro → reproducir/pausar video + mostrar overlay
      if (this.touchIsHorizontal && absDx > 20) {
        e.preventDefault()            // evitamos que esto se considere tap
        this.toggleVideo(index)
        this.activeIndex = index      // fuerza el overlay/mask como hover
        return
      }

      // 2) Tap (casi sin movimiento) → abrir detalle
      if (absDx < 10 && absDy < 10) {
        e.preventDefault() // evitamos click fantasma
        const post = this.posts[index]
        if (post) this.openDialog(post, index)
        return
      }

      // 3) Movimiento mayormente vertical → lo interpretamos como scroll, no hacemos nada
      // no llamamos preventDefault, para no bloquear el scroll
    },

    // ===== helpers media =====
    firstMedia (post) {
      const arr = Array.isArray(post && post.media) ? post.media : []
      return arr.length ? arr[0] : null
    },
    fullUrl (path) {
      if (!path) return ''
      return /^https?:\/\//i.test(path) ? path : (this.FILES_BASE_URL + path)
    },
    isVideo (path) {
      return /\.mp4($|\?)/i.test(path || '')
    },

    // ===== hover (desktop) =====
    playVideo (index) {
      if (this.isTouch) return
      const v = this.$refs['video-' + index]
      const el = Array.isArray(v) ? v[0] : v
      if (el && el.play) {
        try { el.currentTime = 0 } catch (e) {}
        el.play().catch(() => {})
      }
    },
    stopVideo (index) {
      if (this.isTouch) return
      const v = this.$refs['video-' + index]
      const el = Array.isArray(v) ? v[0] : v
      if (el && el.pause) {
        el.pause()
        try { el.currentTime = 0 } catch (e) {}
      }
    },

    // ===== tap (mobile) toggleVideo desde swipe horizontal =====
    toggleVideo (index) {
      const v = this.$refs['video-' + index]
      const el = Array.isArray(v) ? v[0] : v
      if (!el) return
      if (el.paused) {
        el.play().catch(() => {})
      } else {
        el.pause()
      }
    },





    // ===== auto-pause cuando sale del viewport =====
    mountVideoObserver () {
      const root = this.$refs.masonryRoot
      if (!root) return

      if (this.vio) this.vio.disconnect()

      this.vio = new IntersectionObserver((entries) => {
        for (const e of entries) {
          const el = e.target
          if (!el) continue
          // si no es visible, pausa
          if (!e.isIntersecting && !el.paused) {
            try { el.pause() } catch (_) {}
          }
        }
      }, {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0.1
      })

      root.querySelectorAll('video').forEach(v => this.vio.observe(v))
    },

    // ===== Masonry =====
    getGridMetrics () {
      const root = this.$refs.masonryRoot
      if (!root) return { row: 1, gap: 0 }
      const cs = getComputedStyle(root)
      const row = parseFloat(cs.getPropertyValue('grid-auto-rows')) || 1
      const gap =
        parseFloat(cs.getPropertyValue('grid-row-gap')) ||
        parseFloat(cs.getPropertyValue('row-gap')) ||
        parseFloat(cs.getPropertyValue('gap')) ||
        0
      return { row, gap }
    },

    setupObservers () {
      const root = this.$refs.masonryRoot
      if (!root) return

      if (this.ro) this.ro.disconnect()
      this.ro = new ResizeObserver(entries => {
        const { row, gap } = this.getGridMetrics()
        for (const e of entries) {
          const card = e.target
          const item = card.closest('.masonry-item')
          if (!item) continue
          const h = card.offsetHeight
          const span = Math.max(1, Math.ceil((h + gap) / (row + gap)))
          item.style.gridRowEnd = `span ${span}`
        }
      })

      root.querySelectorAll('.masonry-item .post-card').forEach(card => {
        this.ro.observe(card)
      })
    },

    attachMediaListeners () {
      const root = this.$refs.masonryRoot
      if (!root) return
      const imgs = root.querySelectorAll('.masonry-item img')
      const vids = root.querySelectorAll('.masonry-item video')

      imgs.forEach(img => {
        if (img.complete) return
        img.addEventListener('load', this.onMediaLoaded, { once: true })
        img.addEventListener('error', this.onMediaLoaded, { once: true })
      })

      vids.forEach(v => {
        if (v.readyState >= 1) return
        v.addEventListener('loadedmetadata', this.onMediaLoaded, { once: true })
        v.addEventListener('loadeddata', this.onMediaLoaded, { once: true })
        v.addEventListener('error', this.onMediaLoaded, { once: true })
      })
    },

    onMediaLoaded () {
      requestAnimationFrame(this.resizeAll)
    },

    onResize () {
      this.resizeAll()
    },

    resizeAll () {
      const root = this.$refs.masonryRoot
      if (!root) return

      const { row, gap } = this.getGridMetrics()
      const items = root.querySelectorAll('.masonry-item')

      items.forEach(el => {
        el.style.gridRowEnd = '' // reset para medir
        const inner = el.querySelector('.post-card')
        if (!inner) return

        const h = inner.offsetHeight
        const span = Math.max(1, Math.ceil((h + gap) / (row + gap)))
        el.style.gridRowEnd = `span ${span}`
      })
    }
  }
}
</script>

<style>
/* ====== Masonry con CSS Grid (orden por filas) ====== */
.masonry{
  --gutter: 20px;
  --row-height: 6px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: var(--row-height);
  grid-gap: var(--gutter);
  grid-auto-flow: row dense;
}

/* Responsivo */
@media (max-width: 1280px){
  .masonry{ grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 960px){
  .masonry{ grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px){
  .masonry{ grid-template-columns: 1fr; }
}

.masonry-item{

}

/* ===== Tarjetas ===== */
.post-card{
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  border-radius: 10px !important;
  width: 100%;
  display: block;
  background: #DADADA;
  cursor: pointer;
}

/* Media ocupa el ancho, altura natural */
.post-card .media,
.post-card .media img,
.post-card .media video{
  position: relative;
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

/* Fallback cuando no hay media */
.post-card .media-fallback{
  width: 100%;
  height: 220px;
  background: #eaeaea;
}

/* ===== Máscara on-hover ===== */
.post-card .mask{
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 59.62%, rgba(0, 0, 0, 0.2) 76.44%);
  opacity: 0;
  transition: opacity 160ms ease;
}
.post-card:hover .mask{ opacity: 1; }

/* ===== Overlay on-hover (abajo-izq) ===== */
.post-card .overlay{
  position: absolute;
  left: 10px;
  bottom: 10px;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 160ms ease, transform 160ms ease;
  pointer-events: none;
}
.post-card:hover .overlay{
  opacity: 1;
  transform: translateY(0);
}

.post-provider{
  font-weight: 300!important;
  font-size:9px!important;
  height: 19.5px!important;
  min-height: 19.5px!important;
  max-height: 19.5px!important;
  padding-top: 3.75px!important;
  padding-right: 7.5px!important;
  padding-bottom: 3.75px!important;
  padding-left: 7.5px!important;
  gap: 3.75px!important;
  border-radius: 3.75px!important;
  color:black!important;
}

.influencer-data-white .v-card__title{
  font-size:15px!important;
  text-transform:none !important;
  font-weight: 400!important;
  line-height: 20px;
  text-decoration: none!important;
  color:white!important;
}
.influencer-data-white .v-card__subtitle{
  font-size:14px!important;
  color:white!important;
  font-weight: 700!important;
  text-transform:none !important;
  text-decoration: none!important;
}

/* No forzamos aspect-ratio para evitar alturas falsas */
/*
.post-card.TikTok_Content,
.post-card.Instagram_Story{ aspect-ratio: 9 / 16; }
.post-card.Instagram_Post,
.post-card.Case_Study{ aspect-ratio: 4 / 5; }
*/

.post-card.is-active .mask{
  opacity: 1;
}
.post-card.is-active .overlay{
  opacity: 1;
  transform: translateY(0);
}

.post-skeleton{
  width: 100%;
  height: 220px!important;
  border-radius: 10px;
}
.skeleton-height1 .v-skeleton-loader__image, .post-skeleton{
  height: 30.5vw!important;
}


.post-card .media{
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  overflow: hidden;
}

/* según el provider, cambias el ratio */
.post-card.TikTok_Content .media,
.post-card.Instagram_Story .media{
  aspect-ratio: 9 / 16;
}
.post-card.Instagram_Post .media,
.post-card.Case_Study .media{ aspect-ratio: 4 / 5; }

/* dentro, imagen/video ocupan todo el contenedor */
.post-card .media img,
.post-card .media video{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
